import { profileFetch } from "../profileSession";
import { API_BASE, getToken, req } from "./transport";
import type { BlogPlacements, BlogPlacementAction } from "../blogPlacements";
export type AdminBlogPost = {
  id: string;
  title: string;
  slug: string;
  category: string | null;
  excerpt: string | null;
  coverImage: string | null;
  body: string;
  sources: string | null;
  tags: string[];
  status: "draft" | "published";
  updatedAt: string;
  authorName?: string | null;
};

export const contentApi = {
  // [#12] UX review feedback — submit after a completed flow; admins review the stream.
  async submitFeedback(input: { context: "order" | "contract" | "rfq" | "post" | "general"; refId?: string; rating: number; comment?: string }) {
    return req<{ ok: boolean; id: string }>("/feedback", { method: "POST", body: JSON.stringify(input) });
  },

  async adminUxFeedback(context?: string) {
    const q = context ? `?context=${encodeURIComponent(context)}` : "";
    return req<{
      rows: { id: string; context: string; refId: string | null; rating: number; comment: string | null; createdAt: string; userName: string | null }[];
      summary: { count: number; avg: number; byContext: { context: string; count: number; avg: number }[] };
    }>(`/admin/feedback${q}`);
  },

  // [#1] Blog CMS — public reads + admin authoring.
  async blogAvailability() { return req<{ blogAvailable: boolean }>("/blog/settings/availability"); },

  async adminSetBlogAvailable(enabled: boolean) {
    return req<{ blogAvailable: boolean }>("/admin/blog/settings/availability", { method: "PUT", body: JSON.stringify({ enabled }) });
  },

  async listBlog() {
    return req<{ placements: BlogPlacements; posts: { id: string; slug: string; title: string; excerpt: string | null; coverImage: string | null; category: string | null; tags: string[]; authorName: string | null; publishedAt: string | null; likeCount: number; likedByMe: boolean }[] }>("/blog");
  },

  async getBlogPost(slug: string) {
    return req<{ post: { id: string; slug: string; title: string; excerpt: string | null; body: string; sources: string | null; coverImage: string | null; category: string | null; tags: string[]; authorName: string | null; publishedAt: string | null; likeCount: number; likedByMe: boolean } }>(`/blog/${encodeURIComponent(slug)}`);
  },

  async setBlogLike(slug: string, liked: boolean) {
    return req<{ likeCount: number; likedByMe: boolean }>(`/blog/${encodeURIComponent(slug)}/like`, {
      method: "PUT", body: JSON.stringify({ liked }),
    }, { rejectUnauthorized: true });
  },

  async adminListBlog() {
    return req<{ posts: AdminBlogPost[]; placements: BlogPlacements; maxPins?: number }>("/admin/blog", {}, { rejectUnauthorized: true });
  },

  async adminSetBlogPlacement(id: string, action: BlogPlacementAction) {
    return req<{ placements: BlogPlacements }>(`/admin/blog/${encodeURIComponent(id)}/placement`, { method: "PATCH", body: JSON.stringify({ action }) }, { rejectUnauthorized: true });
  },

  async adminUploadBlogCover(file: File, signal: AbortSignal) {
    const token = getToken();
    const response = await profileFetch(`${API_BASE}/admin/blog/covers`, {
      method: "POST", credentials: "include", signal,
      headers: { "Content-Type": file.type, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const image: unknown = await response.json().catch(() => null);
    if (!image || typeof image !== "object") throw new Error("Could not upload the photo. Please try again.");
    if (!response.ok) throw new Error("error" in image && typeof image.error === "string" ? image.error : "Could not upload the photo. Please try again.");
    if (!("imageId" in image) || typeof image.imageId !== "string" || !image.imageId) throw new Error("Could not upload the photo. Please try again.");
    return { coverImage: `${API_BASE}/blog/covers/${encodeURIComponent(image.imageId)}` };
  },

  async adminCreateBlog(input: { title: string; excerpt?: string; body?: string; sources?: string; coverImage?: string; category?: string; tags?: string[]; status?: "draft" | "published" }) {
    return req<{ post: any }>("/admin/blog", { method: "POST", body: JSON.stringify(input) });
  },

  async adminUpdateBlog(id: string, input: { title: string; excerpt?: string; body?: string; sources?: string; coverImage?: string; category?: string; tags?: string[]; status?: "draft" | "published" }) {
    return req<{ post: any }>(`/admin/blog/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) });
  },

  async adminDeleteBlog(id: string) {
    return req<{ ok: boolean }>(`/admin/blog/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  async listBlogComments(slug: string) {
    return req<{ comments: { id: string; authorName: string; body: string; createdAt: string }[] }>(`/blog/${encodeURIComponent(slug)}/comments`);
  },

  async postBlogComment(slug: string, body: string) {
    return req<{ comment: { id: string; authorName: string; body: string; createdAt: string } }>(`/blog/${encodeURIComponent(slug)}/comments`, { method: "POST", body: JSON.stringify({ body }) });
  },

  async adminDeleteBlogComment(id: string) {
    return req<{ ok: boolean }>(`/admin/blog/comments/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // [#11] Support FAQ — public read + admin CRUD.
  async listFaq() {
    return req<{ faqs: { id: string; question: string; answer: string; keywords: string; category: string | null }[] }>("/faq");
  },

  async adminListFaq() {
    return req<{ faqs: any[] }>("/admin/faq");
  },

  async adminCreateFaq(input: { question: string; answer: string; keywords?: string; category?: string; orderIndex?: number; active?: boolean }) {
    return req<{ faq: any }>("/admin/faq", { method: "POST", body: JSON.stringify(input) });
  },

  async adminUpdateFaq(id: string, input: { question: string; answer: string; keywords?: string; category?: string; orderIndex?: number; active?: boolean }) {
    return req<{ faq: any }>(`/admin/faq/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(input) });
  },

  async adminDeleteFaq(id: string) {
    return req<{ ok: boolean }>(`/admin/faq/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // v3.9.22 — public homepage banners
  async banners(slot?: string, signal?: AbortSignal) {
    const r = await req<{ banners: any[] }>(`/banners${slot ? `?slot=${encodeURIComponent(slot)}` : ""}`, { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !Array.isArray(r?.banners)) throw new Error("Missing banners.");
    return r?.banners ?? [];
  },

  async adminBanners() {
    const r = await req<{ banners: any[] }>("/admin/banners");
    return r?.banners ?? [];
  },

  async adminCreateBanner(b: Record<string, unknown>) {
    return req<{ banner: any }>("/admin/banners", { method: "POST", body: JSON.stringify(b) });
  },

  async adminDeleteBanner(id: string) {
    return req<{ ok: boolean }>(`/admin/banners/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  async adminUpdateBanner(id: string, b: Record<string, unknown>) {
    return req<{ banner: any }>(`/admin/banners/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(b) });
  }
};
